import { optionsPourParent, viderLesDependants } from '@taki/data-model';
import type { Cell, Column, EditorKind, Option, Tone } from './contract.js';

/**
 * LES DERIVEURS DE CHAMPS : d'un `taki.fields.json` vers des colonnes.
 *
 * CE QUE CE MODULE EST. Une bibliotheque de fonctions PURES qui transforment de
 * la DONNEE (le fichier de champs d'une app) en objets du contrat des vues
 * (`Column`). Rien de plus.
 *
 * CE QU'IL N'EST PAS, ET C'EST LA LIGNE ROUGE DU CHANTIER. Ce n'est pas un
 * moteur qui INTERPRETE une specification d'ecran. Il ne lit aucune expression,
 * n'evalue aucun gabarit, ne construit aucune vue : il rend des colonnes qu'une
 * app compose, remplace ou enrichit comme elle veut. La difference tient en une
 * phrase : ce module ne sait faire que ce que le fichier DECLARE, et le fichier
 * ne declare que des faits (une clef, un libelle, un type, un catalogue). Le
 * jour ou une clef du fichier decrirait un CALCUL, ce module deviendrait un
 * langage maison, et un langage maison degrade exactement ce que les agents
 * savent le mieux faire : du React et du TypeScript ordinaires.
 *
 * COMMENT UNE APP S'EN ECARTE : elle passe ses propres colonnes. `colonnesDe`
 * rend un tableau ordinaire ; une app le filtre, le reordonne, remplace une
 * entree par la sienne, ou ajoute un `render` par-dessus. Aucune de ces trois
 * choses ne demande la permission du socle. Il n'y a pas de cas ou l'app doit
 * "contourner" ce module : il ne fait rien qu'on ne puisse refaire a la main.
 *
 * OU IL VIT, ET POURQUOI ICI. Dans `views/`, a cote du contrat qu'il produit :
 * son entree vient de `@taki/data-model` (le schema du fichier), sa sortie est
 * un `Column` de `./contract.js`. Le mettre dans une app le rendrait
 * inutilisable par les autres ; le mettre dans le data-model y ferait entrer le
 * vocabulaire d'affichage.
 */

/* ------------------------------------------------------------------ */
/* La forme consommee, decrite ici et importee de nulle part            */
/* ------------------------------------------------------------------ */

/**
 * LE TYPE D'UN CHAMP, tel que le contrat des vues en a besoin.
 *
 * POURQUOI IL EST REDECLARE ICI plutot qu'importe de `@taki/data-model` : ce
 * package est le DESIGN SYSTEM. Il ne connait aucune entite metier, aucune
 * famille, aucun schema Zod, et lui donner une dependance vers le modele de
 * donnees pour dix noms de types echangerait une frontiere nette contre une
 * commodite.
 *
 * CE N'EST PAS UN FAIT ECRIT DEUX FOIS, parce que le compilateur relie les
 * deux : une app qui passe ses `AppField[]` (issus du schema Zod) a
 * `colonnesDe` ne compile QUE si les deux formes concordent. Le jour ou le
 * schema du data-model gagne une valeur de `type`, ce fichier cesse de compiler
 * chez tous ses appelants. C'est du typage structurel, pas une copie.
 */
export type TypeDeChamp =
  | 'text'
  | 'textarea'
  | 'number'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'select'
  | 'multiselect'
  | 'email'
  | 'phone'
  | 'url'
  | 'lien'
  /* UNE PERSONNE DE L'ORGANISATION : la valeur est son `personId`. Le socle
     lui donne une reference implicite vers l'annuaire des personnes
     (`views/screens.ts`), et elle suit ensuite tout le chemin d'un `lien`. */
  | 'personne';

/**
 * LA CIBLE D'UN CHAMP `lien` : la famille qu'il designe, telle que la
 * declaration la porte (`reference` d'un champ de `taki.fields.json`, ou d'une
 * colonne native de `taki.screens.json`).
 *
 * C'EST CE QUI REND UNE COLONNE CONNECTABLE SANS PORTE : avant, une colonne ne
 * designait une fiche que si une porte du meme ecran portait sa famille (le
 * sens inverse d'un lien, `liensEntrants`). Ici la colonne le DIT elle-meme, et
 * l'ecran n'a plus a monter la famille visee pour en afficher un nom.
 *
 * `titre` et `sousTitre` se lisent DANS LE VOCABULAIRE DE LA FAMILLE VISEE :
 * `socle:name` designe `name` a la racine de la fiche liee, `libelle` designe
 * `customFields.libelle` de cette meme fiche. Absents, le titre suit la
 * convention de la famille (`FonctionsDeclarees.titres`), puis l'id.
 */
export interface ReferenceDeclaree {
  family: string;
  type?: string;
  titre?: string;
  sousTitre?: string;
}

/** Une option de catalogue, telle que le fichier de champs la declare. */
export interface OptionDeclaree {
  archived?: boolean;
  value: string;
  label: string;
  /** Une clef de la grille du design ou un hex (`couleurs.ts`). */
  tone?: Tone;
  /** La valeur du champ parent a laquelle l'option appartient (`dependDe`). */
  parent?: string;
}

/** Un champ declare par une app, reduit a ce que les vues en font. */
export interface ChampDeclare {
  key: string;
  label: string;
  type: TypeDeChamp;
  required?: boolean;
  options?: readonly OptionDeclaree[];
  helpText?: string;
  /** La cible d'un champ `lien`, et rien pour les autres types. */
  reference?: ReferenceDeclaree;
  /**
   * L'EXEMPLE QU'UNE CASE VIDE MONTRE (« Sony », « PXW-FX9 », « 290 »).
   *
   * IL SE DECLARE DANS LE STUDIO, champ par champ, et il ne se devine pas : un
   * defaut fabrique depuis le libelle (« Marque... ») repeterait l'etiquette
   * juste au-dessus. Sans lui, la fiche de creation retombe sur le mot generique
   * de son type (`placeholderParDefaut`, `lib/creation.tsx`).
   */
  placeholder?: string;
  /** La clef du champ a choix dont les options de celui-ci dependent. */
  dependDe?: string;
}

/* ------------------------------------------------------------------ */
/* Traduire le vocabulaire                                             */
/* ------------------------------------------------------------------ */

/**
 * LES CHAMPS D'UN JEU, par sa famille et son type local.
 *
 * Rend un tableau VIDE quand le jeu n'est pas declare, jamais une exception :
 * une app peut monter une porte sur un jeu qu'elle ne personnalise pas, et
 * c'est un fait, pas une faute.
 */
export function champsDuJeu(
  fichier: { jeux: readonly { family: string; type?: string; champs: readonly ChampDeclare[] }[] },
  family: string,
  type?: string,
): readonly ChampDeclare[] {
  const jeu = fichier.jeux.find((j) => j.family === family && j.type === type);
  return jeu?.champs ?? [];
}

/**
 * LE TYPE DE DONNEE devient un TYPE D'EDITEUR.
 *
 * Deux vocabulaires, et ils ne se confondent pas : `CustomFieldType` dit ce que
 * la valeur EST (orientee donnee), `EditorKind` dit comment on la
 * SAISIT (ce que le socle sait rendre aujourd'hui). Une table
 * pure les relie, et c'est le seul endroit du depot ou la traduction existe.
 *
 * `boolean` réutilise le choix simple Oui/Non. La colonne convertit les
 * valeurs du sélecteur en booléens avant de les transmettre à l'accès.
 *
 * `multiselect` A LE SIEN depuis les plans recurrents (leurs jours de semaine) :
 * la meme liste que le catalogue, ou chaque clic COCHE ou DECOCHE au lieu de
 * choisir. Il n'est modifiable, comme tous, que si l'app fournit l'ecriture.
 *
 * `lien` PART EN `text` ICI, ET C'EST L'ASSEMBLEUR QUI LE RELEVE : l'editeur
 * `lien` (un choix dans un annuaire) n'a de sens qu'avec la REFERENCE de la
 * colonne, que seul `catalogueDeColonnes` pose (`referencer`, `screens.ts`).
 * Une app TypeScript qui appelle `colonnesDe` a la main sans passer par lui
 * garde une case ou l'on tape un id : un editeur qui promet un annuaire qu'il
 * n'a pas serait le mensonge d'au-dessus.
 */
const EDITEUR_PAR_TYPE: Record<TypeDeChamp, EditorKind | undefined> = {
  text: 'text',
  textarea: 'multiline',
  number: 'number',
  boolean: 'select',
  date: 'date',
  datetime: 'datetime',
  select: 'select',
  multiselect: 'multiselect',
  email: 'text',
  phone: 'text',
  url: 'text',
  lien: 'text',
  /* Comme `lien` : l'assembleur le releve en `lien` des que la colonne porte sa
     reference et s'ecrit (`catalogueDeColonnes`), donc un choix par nom. */
  personne: 'text',
};

/** Le type d'editeur d'un champ, ou `undefined` si le socle ne sait pas le saisir. */
export const editeurDe = (champ: ChampDeclare): EditorKind | undefined =>
  EDITEUR_PAR_TYPE[champ.type];

/** Le catalogue d'un champ de choix, au format du contrat des vues. */
export function libelleOption(option: { label: string; archived?: boolean }): string {
  return option.archived ? `${option.label} (archivée)` : option.label;
}

export function optionsDe(champ: ChampDeclare): Option[] {
  if (champ.type === 'boolean') return [
    { value: 'true', label: 'Oui', tone: 'neutral' },
    { value: 'false', label: 'Non', tone: 'neutral' },
  ];
  return (champ.options ?? []).map((o) => ({
    value: o.value,
    label: libelleOption(o),
    ...(o.archived ? { archived: true } : {}),
    tone: o.tone ?? 'neutral',
  }));
}

/* ------------------------------------------------------------------ */
/* Deriver des colonnes                                                */
/* ------------------------------------------------------------------ */

/**
 * LES CLEFS D'UN CHOIX MULTIPLE, lues dans une cellule.
 *
 * UNE SEULE LECTURE POUR TROIS USAGES, et c'est pour ca qu'elle est exportee :
 * le RENDU (« Lundi, Vendredi »), l'EDITEUR (quelles cases sont cochees) et
 * l'ECRITURE (la liste qui part au SDK) doivent decouper la meme valeur de la
 * meme facon. Trois decoupages ecrits a la main divergeraient au premier
 * separateur oublie, et la coche mentirait sur ce qui est stocke.
 *
 * LA VALEUR ARRIVE UNE CLEF PAR LIGNE (la lecture du socle rend une liste
 * ainsi, `cellule` dans `app-runtime/src/acces.ts`), ou collee par des virgules
 * chez une app qui la lit elle-meme. Vide, `null` ou blanc : aucune clef.
 */
export function clefsDuChoixMultiple(valeur: Cell | undefined): string[] {
  if (valeur === null || valeur === undefined) return [];
  return String(valeur)
    .split(/[\n,]/)
    .map((clef) => clef.trim())
    .filter((clef) => clef !== '');
}

/**
 * CE QU'UNE ECRITURE DE CHAMP PEUT PORTER : une cellule, ou une LISTE de clefs.
 *
 * LA LISTE N'EXISTE QU'ICI, a la frontiere avec l'app, et jamais dans les vues :
 * un choix multiple se stocke en tableau (le schema de la famille l'exige pour
 * un natif, et un sac ne doit pas relire « lun\nven » comme une seule clef),
 * mais il circule dans le moteur sous sa forme de cellule. Seule la colonne
 * `multiselect` en emet une (`colonnesDe`), toujours un tableau NEUF, vide
 * compris : decocher tout ecrit `[]`, pas `null`.
 */
export type ValeurEcrite = Cell | readonly string[];

/** Ce que l'app doit fournir : lire et ecrire le sac de son enregistrement. */
export interface AccesChamps<T> {
  /** La valeur brute d'une clef du sac, telle qu'elle est stockee. */
  lire(record: T, key: string): Cell;
  /**
   * L'ecriture d'une clef. ABSENTE = les colonnes derivees ne portent pas de
   * `set`, donc rien n'est modifiable : c'est le regime d'une app en lecture.
   */
  ecrire?(record: T, key: string, valeur: ValeurEcrite): Promise<void>;
}

/**
 * Ce qu'on peut regler par colonne, sans reecrire la colonne entiere.
 *
 * TYPE PAR L'ENREGISTREMENT, et pas par `unknown` : un helper de socle qui
 * oblige son appelant a caster n'a pas fait son travail, il l'a deplace.
 */
export interface ReglagesColonne<T> {
  /** Colonne triable au clic. Defaut : vrai, sauf pour un texte long. */
  sortable?: boolean;
  /** Agregat de pied de colonne. */
  agg?: 'sum' | 'count' | 'avg';
  /** Habillage seul. Le tri, l'export et l'agregat gardent la valeur brute. */
  render?: Column<T>['render'];
}

/**
 * UNE COLONNE PAR CHAMP DECLARE, dans l'ordre du fichier.
 *
 * CE QU'ELLE PORTE : la clef, le libelle, la lecture, le type d'editeur, le
 * catalogue, et l'ecriture quand l'app en fournit une. C'est exactement ce
 * qu'une app ecrivait a la main, colonne par colonne, avec le meme corps a
 * chaque fois.
 *
 * CE QU'ELLE NE PORTE PAS : aucun `render`. Le formatage est une decision
 * d'app (des euros, une duree, une initiale), et le fichier ne le declare pas.
 * Une app qui veut habiller une colonne la reprend apres coup :
 *
 *     const colonnes = colonnesDe(champs, acces).map((c) =>
 *       c.key === 'dailyRate' ? { ...c, render: (a) => formatEuros(...) } : c,
 *     );
 *
 * `sortable` vaut vrai par defaut, sauf sur un texte long : trier sur un
 * paragraphe n'a jamais aide personne, et l'en-tete cliquable promettrait un
 * classement qui ne veut rien dire.
 */
export function colonnesDe<T>(
  champs: readonly ChampDeclare[],
  acces: AccesChamps<T>,
  reglages: Readonly<Record<string, ReglagesColonne<T>>> = {},
): Column<T>[] {
  return champs.map((champ) => {
    const regle = reglages[champ.key] ?? {};
    /* UN CHOIX MULTIPLE SANS CATALOGUE NE S'EDITE PAS : sa liste s'ouvrirait
       vide, une case qui invite au clic pour ne rien proposer. Il reste
       lisible, en lecture seule. */
    const sansCatalogue = champ.type === 'multiselect' && (champ.options ?? []).length === 0;
    const editeur = sansCatalogue ? undefined : editeurDe(champ);
    const ecrire = acces.ecrire;

    const colonne: Column<T> = {
      key: champ.key,
      label: champ.label,
      value: (record) => acces.lire(record, champ.key),
      sortable: regle.sortable ?? champ.type !== 'textarea',
    };
    if (champ.type === 'boolean') colonne.valueType = 'boolean';
    if (regle.agg !== undefined) colonne.agg = regle.agg;
    /* UN CHOIX MULTIPLE SE LIT PAR SES LIBELLES (« Lundi, Vendredi »), pas par
       ses clefs : la valeur arrive en liste, une ligne par clef (ou collee par
       des virgules chez une app qui la lit elle-meme). La valeur brute reste
       celle du tri et de l'export. */
    if (champ.type === 'multiselect' && (champ.options ?? []).length > 0) {
      const libelles = new Map((champ.options ?? []).map((o) => [o.value, libelleOption(o)]));
      colonne.render = (record) =>
        clefsDuChoixMultiple(acces.lire(record, champ.key))
          .map((clef) => libelles.get(clef) ?? clef)
          .join(', ');
    }
    if (regle.render !== undefined) colonne.render = regle.render;
    /*
     * LE PLACEHOLDER ET L'OBLIGATION VOYAGENT AVEC LA COLONNE, en DONNEE : ce
     * sont deux faits du champ, et la fiche de creation est le seul endroit qui
     * les rende. Les redemander au fichier de champs depuis la fiche
     * l'obligerait a connaitre `taki.fields.json`, qu'elle ne voit pas : elle ne
     * recoit que des colonnes.
     */
    if (champ.placeholder !== undefined) colonne.placeholder = champ.placeholder;
    if (champ.required === true) colonne.required = true;
    if (editeur !== undefined) {
      colonne.kind = editeur;
      /*
       * LE CATALOGUE EST UNE FONCTION, pas un tableau, parce que le contrat le
       * veut ainsi : il se resout au RUNTIME. Aujourd'hui il rend le defaut
       * declare par l'app ; le jour ou un client redefinit ses valeurs, c'est
       * CE point precis qui ira les chercher, et aucune app n'aura a changer.
       */
      if (editeur === 'select') {
        const catalogue = optionsDe(champ);
        colonne.options = () => catalogue;
        /*
         * LE LIBELLE D'UNE VALEUR DE CATALOGUE S'AFFICHE, ET LA VALEUR STOCKEE
         * SE TRIE. Ce n'est pas un formatage decide par l'app : c'est le SENS du
         * catalogue, et l'app ne fait que le declarer. Sans ce rendu, une
         * colonne a choix montrait la clef brute (« bonEtat », « a_reviser »),
         * et chaque app se reecrivait le meme habillage d'une ligne. Une app nee
         * du Studio, elle, n'a aucun TypeScript ou l'ecrire : ses pastilles
         * restaient illisibles.
         *
         * `render` est un rendu SEUL : le tri, l'export et l'agregat continuent
         * de porter `value`, donc la valeur stockee. Un habillage nomme par la
         * declaration le remplace, et c'est ce que `catalogueDeColonnes` fait
         * apres coup.
         */
        colonne.render = (record) => {
          const brut = acces.lire(record, champ.key);
          if (brut === null || brut === undefined || brut === '') return '';
          const cherche = String(brut);
          return catalogue.find((o) => o.value === cherche)?.label ?? cherche;
        };
      }
      if (editeur === 'multiselect') {
        /* LE MEME CATALOGUE, pour la meme raison : l'editeur coche parmi lui.
           Le rendu, lui, est deja pose plus haut (les libelles, separes par
           des virgules). */
        const catalogue = optionsDe(champ);
        colonne.options = () => catalogue;
      }
      if (ecrire !== undefined) {
        colonne.set =
          champ.type === 'boolean'
            ? async (record, valeur) => {
                if (valeur !== true && valeur !== false && valeur !== 'true' && valeur !== 'false') {
                  throw new Error('Choisis Oui ou Non.');
                }
                await ecrire(record, champ.key, valeur === true || valeur === 'true');
              }
            : editeur === 'multiselect'
            ? /* LA CELLULE REDEVIENT UNE LISTE ICI, ET NULLE PART AILLEURS :
                 c'est le seul endroit qui sache encore que le champ est un
                 choix multiple. L'editeur emet les clefs une par ligne (ou
                 `null` quand tout est decoche), l'app recoit un TABLEAU, vide
                 compris, qu'elle ecrit tel quel. */
              (record, valeur) => ecrire(record, champ.key, clefsDuChoixMultiple(valeur))
            : (record, valeur) => ecrire(record, champ.key, valeur);
      }
    }
    return colonne;
  });
}

/**
 * LES OPTIONS DE TRI d'un ecran, derivees des champs triables.
 *
 * L'app y ajoute les siennes (un champ natif, une date du socle) : ce module
 * n'a rien a dire des champs qu'il ne connait pas.
 */
export function trisDe(champs: readonly ChampDeclare[]): { key: string; label: string }[] {
  return champs
    .filter((champ) => champ.type !== 'textarea')
    .map((champ) => ({ key: champ.key, label: champ.label }));
}

/**
 * LES CHAMPS OBLIGATOIRES d'un jeu, pour un formulaire de creation.
 *
 * Le socle ne fabrique pas le formulaire : il dit ce qui manque. Ce qu'on fait
 * d'un champ vide (bloquer, avertir, remplir par defaut) est une decision d'app.
 */
export const requisDe = (champs: readonly ChampDeclare[]): readonly ChampDeclare[] =>
  champs.filter((champ) => champ.required === true);

/**
 * LES CHOIX DEPENDANTS, BRANCHES SUR LES COLONNES DEJA BATIES (`dependDe`,
 * `@taki/data-model`). La colonne d'un champ dependant gagne son catalogue par
 * fiche (`optionsPour`) ; celle de son parent dit ce qu'elle vide en changeant
 * (`videAussi`). Une colonne absente ne recoit rien : l'ecran ne la montre pas.
 */
export function brancherLesDependances<T>(
  colonnes: ReadonlyMap<string, Column<T>>,
  champs: readonly ChampDeclare[],
  acces: AccesChamps<T>,
): void {
  const lire = (record: T, key: string): unknown => {
    const valeur = acces.lire(record, key);
    return champs.find((champ) => champ.key === key)?.type === 'multiselect' ? clefsDuChoixMultiple(valeur) : valeur;
  };
  for (const champ of champs) {
    if (champ.dependDe === undefined) continue;
    const parent = champ.dependDe;
    const colonne = colonnes.get(champ.key);
    if (colonne !== undefined && colonne.options !== undefined) {
      colonne.optionsPour = (record) => optionsDe({ ...champ, options: optionsPourParent(champ, lire(record, parent)) });
    }
    const colonneParent = colonnes.get(parent);
    if (colonneParent !== undefined && colonneParent.videAussi === undefined) {
      colonneParent.videAussi = (record, valeur) => viderLesDependants(champs, parent, valeur, (key) => lire(record, key));
    }
  }
}